import { normalizeQcm } from '../normalizers/normalizeQcm';
import { normalizeVraiFaux } from '../normalizers/normalizeVraiFaux';
import { isVraiFaux, isTableExercise, estImagePorteuse } from './groupQuestions';
import { largeurReponse, longueurTexte, besoinItemPx } from './grilleReponses';

/**
 * Axe FORMAT de l'aperçu : « Réponses : à cocher / libres ».
 *
 * Un QCM en réponse libre masque ses choix et les remplace par des lignes de
 * réponse dimensionnées (courte → 1 ligne, longue → plusieurs). Un tableau à
 * cocher (V/F, colonnes nommées) devient une liste : chaque ligne garde une
 * courte ligne pointillée où l'élève écrit le libellé de la colonne (Vrai /
 * Faux, « Inférieur à 1 »…) ; un vrai V/F demande en plus une justification
 * (ligne pleine largeur sous chaque affirmation). La question reste celle de
 * la banque : la conversion est LOCALE à l'aperçu, jamais persistée.
 *
 * Non convertibles (le support EST la question) :
 *   - QCM à choix images ;
 *   - tableaux d'association (bijection ligne/colonne : les valeurs candidates
 *     disparaîtraient, l'exercice changerait de nature).
 */

/** Seuil (caractères) sous lequel la bonne réponse tient sur une ligne. */
export const QCM_LIBRE_SHORT_THRESHOLD = 10;

const normaliserLibelle = (s) => String(s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

/** Les colonnes sont-elles un vrai Vrai/Faux ? (par LIBELLÉS — `config.type
 *  === 'vraiFaux'` ne le garantit pas : Constructible / Non constructible,
 *  Positif / Négatif, deux décimaux… existent avec ce type.) */
export const estColonnesVraiFaux = (colonnes) => {
    if (!Array.isArray(colonnes) || colonnes.length !== 2) return false;
    const libelles = colonnes.map(normaliserLibelle);
    return libelles.includes('vrai') && libelles.includes('faux');
};

// ─── QCM ────────────────────────────────────────────────────────────

export const qcmConvertible = (payload) => {
    if (payload?.type !== 'qcm') return false;
    if (isVraiFaux(payload) || isTableExercise(payload) || estImagePorteuse(payload)) return false;
    return !normalizeQcm(payload).hasImages;
};

/** La bonne réponse (la plus longue s'il y en a plusieurs) est-elle courte ? */
export const reponseQcmCourte = (payload) => {
    const { options, correctLetters } = normalizeQcm(payload);
    const ok = new Set(correctLetters);
    const maxLen = options.reduce((m, o) => {
        if (!ok.has(o.letter)) return m;
        return Math.max(m, o.kind === 'text' ? String(o.content || '').length : 100);
    }, 0);
    return maxLen <= QCM_LIBRE_SHORT_THRESHOLD;
};

// ─── Tableaux à cocher ──────────────────────────────────────────────

/** Exercice-tableau (V/F ou tableau générique), routé vers PrintVraiFaux. */
export const estTableau = (payload) => isVraiFaux(payload) || isTableExercise(payload);

const elementTable = (payload) => {
    const elements = payload?.elements_additionnels?.elements || [];
    return elements.find((e) => e?.type === 'table' && e.config?.type === 'vraiFaux')
        || elements.find((e) => e?.type === 'table')
        || null;
};

export const tableConvertible = (payload) => {
    if (!estTableau(payload)) return false;
    const cfg = elementTable(payload)?.config;
    if (!cfg || cfg.type === 'association') return false;
    const n = Array.isArray(cfg.lignes) ? cfg.lignes.length : 0;
    return n > 0 && Array.isArray(cfg.bonnesReponses) && cfg.bonnesReponses.length === n;
};

/** Données partagées rendu / corrigé / estimation d'un tableau en libre. */
export const reponseTable = (payload) => {
    const { lignes, colonnes, correctIndices } = normalizeVraiFaux(payload);
    return { lignes, colonnes, correctIndices, vraiFaux: estColonnesVraiFaux(colonnes) };
};

// ─── Liste de réponses d'un tableau (grille réutilisable) ───────────

export { largeurReponse } from './grilleReponses';

/**
 * Données d'un tableau rendu en LISTE (réponse libre sur la feuille, ou mode
 * cahier) : lignes, colonnes, bonnes réponses, nature V/F (par libellés),
 * largeur de la ligne de réponse (0 si `sansLigne` : cahier) et largeur
 * NÉCESSAIRE d'un item pour la grille `.ep-grille-reponses`.
 */
export const besoinTableLibre = (payload, { sansLigne = false } = {}) => {
    const { lignes, colonnes, correctIndices, vraiFaux } = reponseTable(payload);
    const repW = sansLigne ? 0 : largeurReponse(colonnes, vraiFaux);
    const texteMax = lignes.reduce((m, l) => Math.max(m, longueurTexte(l)), 0);
    return {
        lignes, colonnes, correctIndices, vraiFaux, repW,
        n: lignes.length,
        besoin: besoinItemPx({ texteMax, repW }),
    };
};

// ─── Diagnostic par bloc (contrôles) ────────────────────────────────

const RAISON_IMAGES = 'QCM à images : les choix sont conservés.';
const RAISON_ASSOCIATION = 'Tableau d\'association : les colonnes sont la question, le tableau est conservé.';
const AUCUN = { applicable: false, famille: null, convertible: false, raison: null };

/**
 * Le bloc relève-t-il de l'axe format (`applicable`), de quelle famille
 * ('qcm' | 'tableau' | 'mixte'), et peut-il passer en réponse libre ?
 * `raison` explique un refus.
 */
export const reponseLibreDuBloc = (block) => {
    if (block?.kind === 'figure-group') {
        // Bloc mixte à figure partagée : conversion décidée ITEM par item
        // dans le renderer — le bloc est applicable dès qu'un item est un
        // QCM ou un tableau, convertible dès qu'un item l'est.
        const items = block.items || [];
        const aTable = items.some(({ payload }) => estTableau(payload));
        const aQcm = items.some(({ payload }) => payload?.type === 'qcm' && !estTableau(payload));
        if (!aTable && !aQcm) return AUCUN;
        const convertible = items.some(({ payload }) =>
            qcmConvertible(payload) || tableConvertible(payload));
        const famille = aTable && aQcm ? 'mixte' : (aTable ? 'tableau' : 'qcm');
        return {
            applicable: true,
            famille,
            convertible,
            raison: convertible ? null : (aTable ? RAISON_ASSOCIATION : RAISON_IMAGES),
        };
    }
    if (block?.kind === 'qcm-group') {
        const convertible = (block.items || []).some(({ payload }) => qcmConvertible(payload));
        return { applicable: true, famille: 'qcm', convertible, raison: convertible ? null : RAISON_IMAGES };
    }
    if (block?.kind === 'vf-group') {
        const convertible = (block.items || []).some(({ payload }) => tableConvertible(payload));
        return { applicable: true, famille: 'tableau', convertible, raison: convertible ? null : RAISON_ASSOCIATION };
    }
    const payload = block?.kind === 'single' ? block.payload : null;
    if (!payload) return AUCUN;
    if (estTableau(payload)) {
        const convertible = tableConvertible(payload);
        return { applicable: true, famille: 'tableau', convertible, raison: convertible ? null : RAISON_ASSOCIATION };
    }
    if (payload.type === 'qcm') {
        const convertible = qcmConvertible(payload);
        return { applicable: true, famille: 'qcm', convertible, raison: convertible ? null : RAISON_IMAGES };
    }
    return AUCUN;
};
